import { inject } from '@angular/core';
import { Router, CanMatchFn, Route } from '@angular/router';
import { AuthService } from '@features/auth/services/auth.service';
import { PermissionsService } from '@core/services/permissions.service';
import { PortalModuleId } from '@core/models/permissions.model';

export const authGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isAuthenticated()) return true;

  router.navigate(['/auth/login']);
  return false;
};

export const verificationGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    router.navigate(['/auth/login']);
    return false;
  }

  if (authService.isProfileSetup()) return true;

  router.navigate(['/auth/otp']);
  return false;
};

export const guestGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isAuthenticated()) {
    // Role-aware: teachers go to their module, everyone else to the dashboard.
    router.navigate([authService.getLandingRoute()]);
    return false;
  }

  return true;
};

/**
 * Keeps Dismissal Call teachers scoped to their module: any teacher who lands on
 * an admin/portal route is redirected to their filter page. Non-teachers pass
 * through unchanged, so admin routing is unaffected.
 */
export const teacherGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    router.navigate(['/auth/login']);
    return false;
  }

  if (authService.isDismissalTeacher()) {
    router.navigate(['/dismissal-call/filter']);
    return false;
  }

  return true;
};

/**
 * BE-288 module gate. Attached as `canMatch` on module routes tagged with
 * `data: { module: '<moduleId>' }`. If the user wasn't granted the module, the
 * route simply doesn't match — Angular falls through to the `**` redirect
 * (dashboard) — so ungranted modules are never reachable by URL. Unrestricted
 * admins pass everything. Real enforcement is server-side (BE-288); this only
 * keeps the UI honest.
 */
export const moduleGuard: CanMatchFn = (route: Route) => {
  const permissions = inject(PermissionsService);
  const module = route.data?.['module'] as PortalModuleId | undefined;
  if (!module) return true;
  return permissions.hasModule(module);
};

export const adminGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    router.navigate(['/auth/login']);
    return false;
  }

  const currentUser = authService.currentUser();

  if (currentUser?.roles.includes('WAKI_SUPER_ADMIN') || currentUser?.roles.includes('admin')) return true;

  router.navigate(['/dashboard']);
  return false;
};